import React from "react";
import { GoArrowLeft } from "react-icons/go";
import { GoArrowRight } from "react-icons/go";



interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;
  /** When true, shows pagination UI but disables all controls (e.g. fewer than one page of results). */
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 20, 50, 100],
  itemLabel = "items",
  disabled = false,
}) => {
  const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
  const firstItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const lastItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalItems <= itemsPerPage && !disabled) {
    return null;
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const getVisiblePages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: Array<number | "ellipsis-start" | "ellipsis-end"> = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) pages.push("ellipsis-start");
    for (let page = start; page <= end; page++) pages.push(page);
    if (end < totalPages - 1) pages.push("ellipsis-end");
    pages.push(totalPages);

    return pages;
  };

  const pages = getVisiblePages();

  const controlsDisabled =
    disabled || totalItems <= itemsPerPage;

  return (
    <div
      className="d-flex align-items-center justify-content-between flex-wrap gap-3"
      style={{
        color: "#475569",
        fontSize: "13px",
        opacity: controlsDisabled ? 0.65 : 1,
        pointerEvents: controlsDisabled ? "none" : undefined,
      }}
      aria-disabled={controlsDisabled}
    >
      <div>
        Showing <strong>{firstItem}</strong>-<strong>{lastItem}</strong> of{" "}
        <strong>{totalItems}</strong> {itemLabel}
        <span className="ms-2 text-muted">
          Page {currentPage} of {totalPages}
        </span>
      </div>

      <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
        {onItemsPerPageChange && (
          <label className="d-flex align-items-center gap-2 mb-0">
            <span className="text-muted">Rows per page</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 82 }}
              value={itemsPerPage}
              disabled={controlsDisabled}
              onChange={(event) => onItemsPerPageChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            disabled={controlsDisabled || currentPage === 1}
          >
            <GoArrowLeft /> <span>Prev</span>
          </button>

          {pages.map((page) => (
            typeof page === "number" ? (
              <button
                key={page}
                type="button"
                onClick={() => goToPage(page)}
                disabled={controlsDisabled}
                className={`btn btn-sm ${page === currentPage
                  ? "btn-primary"
                  : "btn-outline-secondary"
                  }`}
                style={{ minWidth: 34 }}
              >
                {page}
              </button>
            ) : (
              <span key={page} className="px-2 text-muted">
                ...
              </span>
            )
          ))}

          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            disabled={controlsDisabled || currentPage === totalPages}
          >
            <span>Next</span><GoArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
